import pocketbaseService from '../services/pocketbaseService';
import { demoRepository } from '../repository/demoRepository';
import { calculations } from './calculations';

const MONEY_TOLERANCE = 0.02;
const TTM_EDGE_TOLERANCE_DAYS = 45;
const DAY_MS = 24 * 60 * 60 * 1000;
let loanPayments = [];
let loanPaymentsLoaded = false;
let lastLoanPaymentRefreshAt = null;

const money = (value) => calculations.roundMoney(Math.abs(Number(value) || 0));
const normaliseFy = (payment) => payment?.financialYear || (payment?.paymentDate ? calculations.getAustralianFinancialYear(payment.paymentDate) : '');
const normaliseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
const readLoanBalance = (loan) => {
  const preferred = loan?.currentLoanBalance;
  if (preferred !== null && preferred !== undefined && preferred !== '' && Number.isFinite(Number(preferred))) {
    return Number(preferred);
  }
  const legacy = loan?.currentBalance;
  return legacy !== null && legacy !== undefined && legacy !== '' && Number.isFinite(Number(legacy)) ? Number(legacy) : 0;
};

/**
 * Current debt must only include active linked loans. Pending facilities are not
 * current debt, while closed/refinanced/archived facilities remain historical.
 * Legacy blank-status loans are treated as active for backward compatibility.
 */
export const getCurrentPropertyLoanPosition = (propertyId) => {
  if (!propertyId) return { balance: 0, source: 'none', loanCount: 0, pendingLoanCount: 0, linkedLoanCount: 0 };

  const linkedLoans = demoRepository.getAllLoans().filter(loan => String(loan?.propertyId || '') === String(propertyId));
  const activeLoans = linkedLoans.filter(loan => {
    if (loan?.archived === true) return false;
    const status = normaliseStatus(loan?.status);
    return status === 'active' || status === '';
  });
  const pendingLoanCount = linkedLoans.filter(loan => normaliseStatus(loan?.status) === 'pending').length;

  if (activeLoans.length > 0) {
    return {
      balance: calculations.roundMoney(activeLoans.reduce((sum, loan) => sum + readLoanBalance(loan), 0)),
      source: 'linked_loans',
      loanCount: activeLoans.length,
      pendingLoanCount,
      linkedLoanCount: linkedLoans.length
    };
  }

  const property = demoRepository.getPropertyById(propertyId);
  const fallback = property?.metadata?.financial?.currentLoanBalance ?? property?.currentLoanBalance;
  const hasFallback = fallback !== null && fallback !== undefined && fallback !== '' && Number.isFinite(Number(fallback));
  return {
    balance: hasFallback ? calculations.roundMoney(Number(fallback)) : 0,
    source: hasFallback ? 'property_financial' : 'none',
    loanCount: 0,
    pendingLoanCount,
    linkedLoanCount: linkedLoans.length
  };
};

export const calculateCurrentTotalLoanBalance = (propertyId = null) => {
  if (propertyId) return getCurrentPropertyLoanPosition(propertyId).balance;
  const currentProperties = demoRepository.getAllProperties().filter(property => {
    const status = normaliseStatus(property?.status);
    return status !== 'archived' && status !== 'sold';
  });
  return calculations.roundMoney(currentProperties.reduce((sum, property) => sum + getCurrentPropertyLoanPosition(property.id).balance, 0));
};

/**
 * Occupancy delegates to the repository's date-aware lease authority.
 * Effective contractual leases are authoritative; stored space occupancy flags
 * are presentation/cache fields only and cannot override the lease position.
 */
export const getPropertyOccupancyPosition = (propertyId, asOf = Date.now()) => {
  const spaces = demoRepository.getSpacesByProperty(propertyId)
    .filter(space => normaliseStatus(space?.status) !== 'archived');
  const currentLeases = typeof demoRepository.getCurrentLeasesByProperty === 'function'
    ? demoRepository.getCurrentLeasesByProperty(propertyId, asOf)
    : [];

  const wholePropertyOccupied = currentLeases.some(lease => !String(lease?.spaceId || lease?.space || '').trim());
  const occupiedSpaceIds = spaces
    .filter(space => normaliseStatus(space?.status) === 'occupied')
    .map(space => String(space.id));
  const hasCurrentLease = currentLeases.length > 0;
  const hasVirtualWholePropertyUnit = spaces.length === 0 && hasCurrentLease;
  const totalSpaces = spaces.length || (hasVirtualWholePropertyUnit ? 1 : 0);
  const occupiedSpaces = spaces.length > 0
    ? occupiedSpaceIds.length
    : (hasVirtualWholePropertyUnit ? 1 : 0);
  const occupancyRate = totalSpaces > 0 ? calculations.roundPercent(occupiedSpaces / totalSpaces) : 0;
  const activeTenantIds = [...new Set(
    currentLeases.map(lease => String(lease?.tenantId || lease?.tenant || '')).filter(Boolean)
  )];

  return {
    propertyId,
    status: hasCurrentLease ? 'Occupied' : 'Vacant',
    hasActiveTenant: hasCurrentLease,
    activeTenantCount: activeTenantIds.length,
    activeLeaseCount: currentLeases.length,
    activeTenantIds,
    occupiedSpaceIds,
    wholePropertyOccupied,
    totalSpaces,
    occupiedSpaces,
    occupancyRate
  };
};

export const getLoanPaymentCashOutflow = (payment) => {
  const deducted = payment?.amountDeducted;
  if (deducted !== null && deducted !== undefined && deducted !== '' && Number.isFinite(Number(deducted))) {
    return money(deducted);
  }
  const repayment = payment?.totalRepayment;
  return repayment !== null && repayment !== undefined && repayment !== '' && Number.isFinite(Number(repayment))
    ? money(repayment)
    : 0;
};

export const getLoanPaymentsByProperty = (propertyId, financialYear = null) => loanPayments.filter(payment => {
  const paymentPropertyId = payment?.propertyId || payment?.property || '';
  if (String(paymentPropertyId) !== String(propertyId || '')) return false;
  return !financialYear || normaliseFy(payment) === financialYear;
});

export const refreshFinanceData = async ({ notify = true } = {}) => {
  if (!pocketbaseService.isEnabled) {
    loanPayments = [];
    loanPaymentsLoaded = true;
    lastLoanPaymentRefreshAt = Date.now();
    return { success: true, mode: 'demo', count: 0 };
  }

  const authState = pocketbaseService.auth.getState();
  if (!authState?.isAuthenticated) {
    return { success: false, mode: 'unauthenticated', count: loanPayments.length };
  }

  const raw = await pocketbaseService.loadCollection('loan_payments');
  loanPayments = (raw || []).map(record => pocketbaseService.mappers.mapPocketBaseToApp('loan_payments', record));
  loanPaymentsLoaded = true;
  lastLoanPaymentRefreshAt = Date.now();

  if (notify && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mpp:data-refreshed', {
      detail: { source: 'finance-data', timestamp: Date.now(), loanPaymentCount: loanPayments.length }
    }));
  }

  return { success: true, mode: 'pocketbase', count: loanPayments.length };
};

export const calculatePropertyFinanceCashFlow = (
  propertyId,
  financialYear = calculations.getAustralianFinancialYear(new Date())
) => {
  const operating = calculations.calculateOperatingPerformance({ propertyIds: [propertyId], financialYear });
  const payments = getLoanPaymentsByProperty(propertyId, financialYear);

  if (payments.length > 0) {
    let financingCashOutflow = 0;
    let interestPaid = 0;
    let principalPaid = 0;
    let feesPaid = 0;
    let splitReviewCount = 0;
    let reconciliationReviewCount = 0;
    let cashFlowImpactReviewCount = 0;
    let missingCashOutflowCount = 0;

    payments.forEach(payment => {
      const cashOutflow = getLoanPaymentCashOutflow(payment);
      financingCashOutflow += cashOutflow;
      interestPaid += money(payment?.interestAmount);
      principalPaid += money(payment?.principalAmount);
      feesPaid += money(payment?.feeAmount);
      if (cashOutflow <= 0) missingCashOutflowCount += 1;

      const splitStatus = normaliseStatus(payment?.splitStatus);
      if (splitStatus !== 'complete_split') {
        splitReviewCount += 1;
      } else {
        const componentTotal = money(payment?.interestAmount) + money(payment?.principalAmount) + money(payment?.feeAmount);
        const statedRepayment = payment?.totalRepayment !== null && payment?.totalRepayment !== undefined && payment?.totalRepayment !== ''
          ? money(payment.totalRepayment)
          : cashOutflow;
        if (Math.abs(componentTotal - statedRepayment) > MONEY_TOLERANCE) reconciliationReviewCount += 1;
      }

      if (payment?.cashFlowImpact !== null && payment?.cashFlowImpact !== undefined && payment?.cashFlowImpact !== '') {
        if (Math.abs(money(payment.cashFlowImpact) - cashOutflow) > MONEY_TOLERANCE) cashFlowImpactReviewCount += 1;
      }
    });

    financingCashOutflow = calculations.roundMoney(financingCashOutflow);
    interestPaid = calculations.roundMoney(interestPaid);
    principalPaid = calculations.roundMoney(principalPaid);
    feesPaid = calculations.roundMoney(feesPaid);
    const reviewCount = splitReviewCount + reconciliationReviewCount + cashFlowImpactReviewCount + missingCashOutflowCount;
    const cashFlowAfterFinance = calculations.roundMoney(operating.netOperatingIncome - financingCashOutflow);

    return {
      propertyId,
      financialYear,
      source: 'loan_payments',
      completeForCashFlow: missingCashOutflowCount === 0,
      splitComplete: splitReviewCount === 0 && reconciliationReviewCount === 0,
      reviewCount,
      netOperatingIncome: operating.netOperatingIncome,
      financingCashOutflow,
      interestPaid,
      principalPaid,
      feesPaid,
      paymentCount: payments.length,
      splitReviewCount,
      reconciliationReviewCount,
      cashFlowImpactReviewCount,
      cashFlowAfterFinance,
      calculationHelp: {
        financingCashOutflow: `${calculations.formatCurrencyAUD(financingCashOutflow)} = total actual loan amounts deducted from cash in FY ${financialYear}. Interest, principal and fees are components of those repayments and are not subtracted a second time.`,
        split: `${calculations.formatCurrencyAUD(interestPaid)} interest + ${calculations.formatCurrencyAUD(principalPaid)} principal + ${calculations.formatCurrencyAUD(feesPaid)} fees are the recorded repayment components.`,
        cashFlowAfterFinance: `${calculations.formatCurrencyAUD(operating.netOperatingIncome)} NOI − ${calculations.formatCurrencyAUD(financingCashOutflow)} financing cash outflow = ${calculations.formatCurrencyAUD(cashFlowAfterFinance)} cash flow after finance.`,
        review: reviewCount > 0
          ? `${reviewCount} loan-payment reconciliation item(s) need review; the cash figure uses the actual amount deducted and does not guess a replacement value.`
          : 'All loan-payment records used for this period reconcile without a flagged split/cash-flow exception.'
      }
    };
  }

  const fallback = calculations.calculateOperatingPerformance({ propertyIds: [propertyId], financialYear });
  const fallbackFinancing = calculations.roundMoney(fallback.financingCosts || 0);
  const cashFlowAfterFinance = calculations.roundMoney(fallback.netOperatingIncome - fallbackFinancing);
  const pendingLoad = pocketbaseService.isEnabled && !loanPaymentsLoaded;
  const loanPosition = calculations.getPropertyLoanPosition(propertyId);
  const financeExpected = loanPosition.loanCount > 0 || loanPosition.balance > 0 || loanPosition.source === 'property_financial';

  if (!pendingLoad && !financeExpected && fallbackFinancing === 0) {
    return {
      propertyId,
      financialYear,
      source: 'no_finance_required',
      completeForCashFlow: true,
      splitComplete: true,
      reviewCount: 0,
      paymentCount: 0,
      financingCashOutflow: 0,
      interestPaid: 0,
      principalPaid: 0,
      feesPaid: 0,
      netOperatingIncome: fallback.netOperatingIncome,
      cashFlowAfterFinance: fallback.netOperatingIncome,
      calculationHelp: {
        financingCashOutflow: 'No active property finance balance or Paid financing cash outflow is recorded for this period, so financing cash outflow is $0.00.',
        split: 'No loan repayment split is required because no active financing cash outflow is recorded.',
        cashFlowAfterFinance: `${calculations.formatCurrencyAUD(fallback.netOperatingIncome)} NOI − $0.00 financing cash outflow = ${calculations.formatCurrencyAUD(fallback.netOperatingIncome)} cash flow after finance.`,
        review: 'No finance-data exception is currently identified for this property and period.'
      }
    };
  }

  return {
    propertyId,
    financialYear,
    source: pendingLoad ? 'finance_data_pending' : (fallbackFinancing > 0 ? 'transactions_fallback' : 'missing_loan_payments'),
    completeForCashFlow: false,
    splitComplete: false,
    reviewCount: 1,
    paymentCount: 0,
    financingCashOutflow: fallbackFinancing,
    interestPaid: 0,
    principalPaid: 0,
    feesPaid: 0,
    netOperatingIncome: fallback.netOperatingIncome,
    cashFlowAfterFinance,
    calculationHelp: {
      financingCashOutflow: pendingLoad
        ? `Loan-payment data has not finished loading. ${calculations.formatCurrencyAUD(fallbackFinancing)} of Paid financing transactions is shown as a provisional fallback.`
        : fallbackFinancing > 0
          ? `${calculations.formatCurrencyAUD(fallbackFinancing)} from Paid financing transactions because no loan-payment records are available for FY ${financialYear}. This fallback can omit principal repayments.`
          : `An active/current loan position exists, but no loan-payment records are available for FY ${financialYear}. Financing cash outflow is therefore not considered complete.`,
      split: 'Interest/principal split is unavailable without loan-payment records.',
      cashFlowAfterFinance: `${calculations.formatCurrencyAUD(fallback.netOperatingIncome)} NOI − ${calculations.formatCurrencyAUD(fallbackFinancing)} recorded financing cash outflow = ${calculations.formatCurrencyAUD(cashFlowAfterFinance)} provisional cash flow after finance.`,
      review: 'Finance cash flow is provisional until loan-payment records are available, because transaction-only data may not contain principal repayments.'
    }
  };
};

export const calculateTrailingTwelveMonthOperatingPerformance = (propertyId, asOf = new Date()) => {
  const end = asOf instanceof Date ? new Date(asOf.getTime()) : new Date(asOf);
  const safeEnd = Number.isNaN(end.getTime()) ? new Date() : end;
  const start = new Date(safeEnd.getTime());
  start.setFullYear(start.getFullYear() - 1);

  let operatingIncome = 0;
  let operatingExpenses = 0;
  let paidTransactionCount = 0;
  let reviewRequired = 0;
  let reviewRequiredCount = 0;
  const operatingActivityTimestamps = [];

  demoRepository.getTransactions().forEach(transaction => {
    if (String(transaction?.propertyId || '') !== String(propertyId || '')) return;
    const rawDate = transaction?.date ?? transaction?.transactionDate;
    if (!rawDate) return;
    const transactionDate = new Date(rawDate);
    if (Number.isNaN(transactionDate.getTime()) || transactionDate < start || transactionDate > safeEnd) return;

    const classified = calculations.classifyTransactionForPerformance(transaction);
    if (!classified.realised) return;
    paidTransactionCount += 1;

    if (classified.classification === 'operating_income') {
      operatingIncome += classified.amount;
      operatingActivityTimestamps.push(transactionDate.getTime());
    } else if (classified.classification === 'operating_expense') {
      operatingExpenses += classified.amount;
      operatingActivityTimestamps.push(transactionDate.getTime());
    } else if (classified.classification === 'review_required') {
      reviewRequired += classified.amount;
      reviewRequiredCount += 1;
    }
  });

  operatingIncome = calculations.roundMoney(operatingIncome);
  operatingExpenses = calculations.roundMoney(operatingExpenses);
  reviewRequired = calculations.roundMoney(reviewRequired);
  const netOperatingIncome = calculations.roundMoney(operatingIncome - operatingExpenses);
  const firstOperatingTimestamp = operatingActivityTimestamps.length > 0 ? Math.min(...operatingActivityTimestamps) : null;
  const lastOperatingTimestamp = operatingActivityTimestamps.length > 0 ? Math.max(...operatingActivityTimestamps) : null;
  const firstCoverageDeadline = start.getTime() + (TTM_EDGE_TOLERANCE_DAYS * DAY_MS);
  const lastCoverageThreshold = safeEnd.getTime() - (TTM_EDGE_TOLERANCE_DAYS * DAY_MS);
  const historyCoverageComplete = firstOperatingTimestamp !== null && lastOperatingTimestamp !== null
    && firstOperatingTimestamp <= firstCoverageDeadline
    && lastOperatingTimestamp >= lastCoverageThreshold;
  const classificationComplete = reviewRequiredCount === 0;
  const coverageDays = firstOperatingTimestamp !== null && lastOperatingTimestamp !== null
    ? Math.max(0, Math.floor((lastOperatingTimestamp - firstOperatingTimestamp) / DAY_MS))
    : 0;

  return {
    propertyId,
    periodStart: start.getTime(),
    periodEnd: safeEnd.getTime(),
    operatingIncome,
    operatingExpenses,
    netOperatingIncome,
    paidTransactionCount,
    reviewRequired,
    reviewRequiredCount,
    firstOperatingTransactionDate: firstOperatingTimestamp,
    lastOperatingTransactionDate: lastOperatingTimestamp,
    coverageDays,
    classificationComplete,
    historyCoverageComplete,
    complete: classificationComplete && historyCoverageComplete
  };
};

const originalGetPropertySummary = calculations.getPropertySummary.bind(calculations);

const describeLoanSource = (loanPosition) => {
  if (loanPosition.source === 'linked_loans') return `${loanPosition.loanCount} active linked loan record${loanPosition.loanCount === 1 ? '' : 's'}`;
  if (loanPosition.source === 'property_financial') return 'property financial details fallback because no active linked loan record is available';
  return 'no recorded current loan balance';
};

const describeRentSource = (rentPosition) => {
  if (rentPosition.source === 'current_contractual_leases') return `${rentPosition.recordCount} current contractual lease record${rentPosition.recordCount === 1 ? '' : 's'}`;
  return 'no current contractual lease rent record';
};

export const getAuthoritativePropertySummary = (
  propertyId,
  financialYear = calculations.getAustralianFinancialYear(new Date())
) => {
  const base = originalGetPropertySummary(propertyId);
  if (!base) return base;

  const property = demoRepository.getPropertyById(propertyId);
  const occupancy = getPropertyOccupancyPosition(propertyId);
  const currentFinancialYear = financialYear && financialYear !== 'All'
    ? financialYear
    : calculations.getAustralianFinancialYear(new Date());
  const marketValue = calculations.roundMoney(Number(property?.currentMarketValue) || 0);
  const totalAcquisitionCost = calculations.calculatePropertyAcquisitionCost(property);
  const loanPosition = calculations.getPropertyLoanPosition(propertyId);
  const totalLoanBalance = loanPosition.balance;
  const equity = calculations.calculateEquity(marketValue, totalLoanBalance);
  const lvr = calculations.calculateLVR(totalLoanBalance, marketValue);
  const gainLossAcquisition = calculations.roundMoney(marketValue - totalAcquisitionCost);
  const occupiedSpaces = occupancy.occupiedSpaces;
  const occupancyRate = occupancy.occupancyRate;
  const rentPosition = calculations.getPropertyAnnualRentPosition(propertyId);
  const annualRent = rentPosition.annualRent;
  const grossYield = calculations.calculateGrossYield(annualRent, marketValue);
  const operating = calculations.calculateOperatingPerformance({ propertyIds: [propertyId], financialYear: currentFinancialYear });
  const finance = calculatePropertyFinanceCashFlow(propertyId, currentFinancialYear);
  const trailingTwelveMonth = calculateTrailingTwelveMonthOperatingPerformance(propertyId);
  const netYield = marketValue > 0
    ? calculations.roundPercent(trailingTwelveMonth.netOperatingIncome / marketValue)
    : 0;
  const netYieldComplete = marketValue > 0 && trailingTwelveMonth.complete;
  const ownershipPercent = Number(property?.ownershipPercent || 100);
  const ownershipBasis = ownershipPercent === 100 ? 'full_property' : 'full_property_not_ownership_adjusted';

  const formulaChecks = {
    equity: calculations.roundMoney(marketValue - totalLoanBalance) === equity,
    lvr: marketValue === 0 || Math.abs(calculations.calculateLVR(totalLoanBalance, marketValue) - lvr) < 0.0001,
    grossYield: marketValue === 0 || Math.abs(calculations.calculateGrossYield(annualRent, marketValue) - grossYield) < 0.0001,
    cashFlowAfterFinance: calculations.roundMoney(operating.netOperatingIncome - finance.financingCashOutflow) === finance.cashFlowAfterFinance
  };

  const dataQualityIssues = [];
  if (marketValue <= 0) dataQualityIssues.push('Current market value is missing or zero, so percentage yields/LVR cannot be meaningfully interpreted.');
  if (!rentPosition.complete) dataQualityIssues.push(`${rentPosition.unsupportedCount} rent record(s) use an unsupported/custom rent basis and are excluded from annualised expected rent.`);
  if (operating.reviewRequiredCount > 0) dataQualityIssues.push(`${operating.reviewRequiredCount} Paid transaction(s) in FY ${currentFinancialYear} require more specific classification before NOI is fully complete.`);
  if (operating.scheduleOtherDeductions > 0) dataQualityIssues.push(`${calculations.formatCurrencyAUD(operating.scheduleOtherDeductions)} of generic deductions on Paid rent-schedule rows is excluded from NOI because no accounting category is recorded.`);
  if (!finance.completeForCashFlow || finance.reviewCount > 0) dataQualityIssues.push(`Finance cash flow for FY ${currentFinancialYear} is provisional or contains a loan-payment reconciliation item requiring review.`);
  if (!trailingTwelveMonth.classificationComplete) dataQualityIssues.push('Trailing-12-month net yield is provisional because one or more Paid transactions require more specific classification.');
  if (!trailingTwelveMonth.historyCoverageComplete) dataQualityIssues.push('Trailing-12-month net yield is provisional because recorded operating activity does not cover both ends of the 12-month measurement window.');
  if (ownershipBasis !== 'full_property') dataQualityIssues.push(`Property figures are shown on a full-property basis; the recorded ${ownershipPercent}% ownership is not automatically applied to debt/equity/cash-flow metrics.`);

  const pendingLoanNote = loanPosition.pendingLoanCount > 0
    ? ` ${loanPosition.pendingLoanCount} pending linked loan record${loanPosition.pendingLoanCount === 1 ? ' is' : 's are'} excluded from current debt until active.`
    : '';

  const calculationHelp = {
    marketValue: `${calculations.formatCurrencyAUD(marketValue)} is the latest current market value recorded for this property. It is an input, not a derived number.`,
    acquisitionCost: `${calculations.formatCurrencyAUD(property?.acquisitionPrice || property?.purchasePrice || 0)} purchase price plus the applicable recorded acquisition-cost fields = ${calculations.formatCurrencyAUD(totalAcquisitionCost)} total acquisition cost. Legacy aggregate acquisition costs and itemised costs are not added together twice.`,
    loanBalance: `${calculations.formatCurrencyAUD(totalLoanBalance)} current loan balance from ${describeLoanSource(loanPosition)}. Linked-loan balances and the property-level fallback are never added together.${pendingLoanNote}`,
    equity: `${calculations.formatCurrencyAUD(marketValue)} market value − ${calculations.formatCurrencyAUD(totalLoanBalance)} current loan balance = ${calculations.formatCurrencyAUD(equity)} equity.`,
    lvr: marketValue > 0
      ? `${calculations.formatCurrencyAUD(totalLoanBalance)} loan balance ÷ ${calculations.formatCurrencyAUD(marketValue)} market value × 100 = ${calculations.formatPercentage(lvr)} LVR.`
      : 'LVR cannot be meaningfully calculated until a non-zero current market value is recorded.',
    gainLossAcquisition: `${calculations.formatCurrencyAUD(marketValue)} market value − ${calculations.formatCurrencyAUD(totalAcquisitionCost)} total acquisition cost = ${calculations.formatCurrencyAUD(gainLossAcquisition)} unrealised gain/loss against acquisition cost.`,
    occupancy: occupancy.hasActiveTenant
      ? `${occupancy.status}: ${occupancy.activeTenantCount} current tenant${occupancy.activeTenantCount === 1 ? '' : 's'} linked through ${occupancy.activeLeaseCount} date-effective contractual lease${occupancy.activeLeaseCount === 1 ? '' : 's'}.${occupancy.totalSpaces > 0 ? ` ${occupiedSpaces} of ${occupancy.totalSpaces} rentable unit${occupancy.totalSpaces === 1 ? '' : 's'} occupied = ${calculations.formatPercentage(occupancyRate)} occupancy.` : ''}`
      : 'Vacant: no date-effective contractual lease applies to this property today, so occupancy is 0%.',
    annualRent: `${calculations.formatCurrencyAUD(annualRent)} expected annual gross rent from ${describeRentSource(rentPosition)}. Each rent amount is annualised using its recorded rent basis (weekly × 52, fortnightly × 26, monthly × 12, quarterly × 4, annual × 1).`,
    grossYield: marketValue > 0
      ? `${calculations.formatCurrencyAUD(annualRent)} expected annual gross rent ÷ ${calculations.formatCurrencyAUD(marketValue)} market value = ${calculations.formatPercentage(grossYield)} gross yield.`
      : 'Gross yield cannot be meaningfully calculated until a non-zero current market value is recorded.',
    operatingIncome: operating.scheduleRentUsed > 0
      ? `${calculations.formatCurrencyAUD(operating.operatingIncome)} realised operating income for FY ${currentFinancialYear}. ${calculations.formatCurrencyAUD(operating.scheduleRentUsed)} rent is sourced from ${operating.paidRentScheduleCount} Paid rent-schedule row(s) because no Paid rent transaction exists for that property/FY. Paid reimbursements remain additional income.`
      : `${calculations.formatCurrencyAUD(operating.operatingIncome)} realised operating income from Paid rent/reimbursement transactions in FY ${currentFinancialYear}.`,
    operatingExpenses: operating.scheduleAgentFeeUsed > 0
      ? `${calculations.formatCurrencyAUD(operating.operatingExpenses)} realised operating expenses for FY ${currentFinancialYear}, including ${calculations.formatCurrencyAUD(operating.scheduleAgentFeeUsed)} of agent-management fees sourced from Paid rent-schedule rows where no Paid management-fee transaction exists.`
      : `${calculations.formatCurrencyAUD(operating.operatingExpenses)} realised operating expenses from Paid operating-cost transactions in FY ${currentFinancialYear}. Financing, capital, acquisition and private items are excluded from NOI.`,
    netOperatingIncome: `${calculations.formatCurrencyAUD(operating.operatingIncome)} operating income − ${calculations.formatCurrencyAUD(operating.operatingExpenses)} operating expenses = ${calculations.formatCurrencyAUD(operating.netOperatingIncome)} NOI for FY ${currentFinancialYear}.`,
    financingCashOutflow: finance.calculationHelp.financingCashOutflow,
    financeSplit: finance.calculationHelp.split,
    cashFlowAfterFinance: finance.calculationHelp.cashFlowAfterFinance,
    netYield: marketValue > 0
      ? `${calculations.formatCurrencyAUD(trailingTwelveMonth.netOperatingIncome)} trailing-12-month NOI ÷ ${calculations.formatCurrencyAUD(marketValue)} current market value = ${calculations.formatPercentage(netYield)} net yield.${netYieldComplete ? '' : ` The result is provisional because ${!trailingTwelveMonth.classificationComplete ? 'classification review is required' : 'the recorded operating history does not span the full measurement window'}.`}`
      : 'Net yield cannot be meaningfully calculated until a non-zero current market value is recorded.',
    ownershipBasis: ownershipBasis === 'full_property'
      ? 'Property metrics are shown on a 100% full-property basis.'
      : `Property metrics are currently shown on a full-property basis. The recorded ownership is ${ownershipPercent}%, but debt/equity/cash-flow are not automatically scaled because loan ownership may not match title ownership.`
  };

  return {
    ...base,
    totalAcquisitionCost,
    totalLoanBalance,
    loanBalanceSource: loanPosition.source,
    activeLoanCount: loanPosition.loanCount,
    pendingLoanCount: loanPosition.pendingLoanCount || 0,
    equity,
    lvr,
    gainLossAcquisition,
    spacesCount: occupancy.totalSpaces,
    occupiedSpaces,
    occupancyRate,
    occupancyStatus: occupancy.status,
    hasActiveTenant: occupancy.hasActiveTenant,
    activeTenantCount: occupancy.activeTenantCount,
    activeLeaseCount: occupancy.activeLeaseCount,
    occupiedSpaceIds: occupancy.occupiedSpaceIds,
    wholePropertyOccupied: occupancy.wholePropertyOccupied,
    annualRent,
    annualRentSource: rentPosition.source,
    annualRentRecordCount: rentPosition.recordCount,
    annualRentUnsupportedCount: rentPosition.unsupportedCount,
    annualRentComplete: rentPosition.complete,
    currentFinancialYear,
    actualOperatingIncome: operating.operatingIncome,
    actualOperatingExpenses: operating.operatingExpenses,
    netOperatingIncome: operating.netOperatingIncome,
    operatingIncomeSource: operating.rentIncomeSource,
    operatingAgentFeeSource: operating.agentFeeSource,
    paidRentScheduleCount: operating.paidRentScheduleCount || 0,
    scheduleRentUsed: operating.scheduleRentUsed || 0,
    scheduleAgentFeeUsed: operating.scheduleAgentFeeUsed || 0,
    scheduleOtherDeductions: operating.scheduleOtherDeductions || 0,
    grossYield,
    nonOperatingIncome: operating.nonOperatingIncome,
    financingCosts: operating.financingCosts,
    capitalOutlays: operating.capitalOutlays,
    acquisitionOutlays: operating.acquisitionOutlays,
    performanceReviewRequired: operating.reviewRequired,
    performanceReviewRequiredCount: operating.reviewRequiredCount,
    financingCashOutflow: finance.financingCashOutflow,
    loanInterestPaid: finance.interestPaid,
    loanPrincipalPaid: finance.principalPaid,
    loanFeesPaid: finance.feesPaid,
    cashFlowAfterFinance: finance.cashFlowAfterFinance,
    netCashFlow: finance.cashFlowAfterFinance,
    financeCashFlowSource: finance.source,
    financeCashFlowComplete: finance.completeForCashFlow,
    financeSplitComplete: finance.splitComplete,
    financeReviewCount: finance.reviewCount,
    trailing12MonthOperatingIncome: trailingTwelveMonth.operatingIncome,
    trailing12MonthOperatingExpenses: trailingTwelveMonth.operatingExpenses,
    trailing12MonthNOI: trailingTwelveMonth.netOperatingIncome,
    trailing12MonthStart: trailingTwelveMonth.periodStart,
    trailing12MonthEnd: trailingTwelveMonth.periodEnd,
    trailing12MonthReviewRequired: trailingTwelveMonth.reviewRequired,
    trailing12MonthReviewRequiredCount: trailingTwelveMonth.reviewRequiredCount,
    trailing12MonthClassificationComplete: trailingTwelveMonth.classificationComplete,
    trailing12MonthHistoryCoverageComplete: trailingTwelveMonth.historyCoverageComplete,
    trailing12MonthFirstOperatingTransactionDate: trailingTwelveMonth.firstOperatingTransactionDate,
    trailing12MonthLastOperatingTransactionDate: trailingTwelveMonth.lastOperatingTransactionDate,
    trailing12MonthCoverageDays: trailingTwelveMonth.coverageDays,
    netYield,
    netYieldBasis: 'trailing_12_month_noi_over_current_market_value',
    netYieldComplete,
    ownershipPercent,
    ownershipBasis,
    formulaChecks,
    formulaChecksPassed: Object.values(formulaChecks).every(Boolean),
    dataQualityIssues,
    dataQualityStatus: dataQualityIssues.length === 0 ? 'complete' : 'review_required',
    calculationHelp,
    operatingCalculationHelp: {
      operatingIncome: calculationHelp.operatingIncome,
      operatingExpenses: calculationHelp.operatingExpenses,
      netOperatingIncome: calculationHelp.netOperatingIncome,
      reviewRequired: operating.scheduleOtherDeductions > 0
        ? `${calculations.formatCurrencyAUD(operating.scheduleOtherDeductions)} of generic deductions on Paid rent-schedule rows is excluded from NOI until those costs are categorised.`
        : operating.reviewRequiredCount > 0
          ? `${operating.reviewRequiredCount} Paid transaction(s), totalling ${calculations.formatCurrencyAUD(operating.reviewRequired)}, are excluded from NOI until categorised more specifically.`
          : `No Paid transaction or uncategorised Paid rent-schedule deduction requires classification review for FY ${currentFinancialYear}.`
    },
    financeCalculationHelp: finance.calculationHelp
  };
};

calculations.getCurrentPropertyLoanPosition = getCurrentPropertyLoanPosition;
calculations.getPropertyLoanPosition = getCurrentPropertyLoanPosition;
calculations.calculateCurrentTotalLoanBalance = calculateCurrentTotalLoanBalance;
calculations.calculateTotalLoanBalance = calculateCurrentTotalLoanBalance;
calculations.getPropertyOccupancyPosition = getPropertyOccupancyPosition;
calculations.getLoanPaymentCashOutflow = getLoanPaymentCashOutflow;
calculations.getLoanPaymentsByProperty = getLoanPaymentsByProperty;
calculations.calculatePropertyFinanceCashFlow = calculatePropertyFinanceCashFlow;
calculations.calculateTrailingTwelveMonthOperatingPerformance = calculateTrailingTwelveMonthOperatingPerformance;
calculations.getAuthoritativePropertySummary = getAuthoritativePropertySummary;
calculations.getPropertySummary = getAuthoritativePropertySummary;
calculations.refreshFinanceData = refreshFinanceData;

const originalRefreshFromPocketBase = demoRepository.refreshFromPocketBase.bind(demoRepository);
demoRepository.refreshFromPocketBase = async (...args) => {
  const result = await originalRefreshFromPocketBase(...args);
  if (result?.success && result?.mode !== 'demo') {
    await refreshFinanceData({ notify: args?.[0]?.notify !== false });
  }
  return result;
};

const originalResetToDemoData = demoRepository.resetToDemoData.bind(demoRepository);
demoRepository.resetToDemoData = (...args) => {
  loanPayments = [];
  loanPaymentsLoaded = true;
  lastLoanPaymentRefreshAt = Date.now();
  return originalResetToDemoData(...args);
};

export const getFinanceDataState = () => ({
  loaded: loanPaymentsLoaded,
  count: loanPayments.length,
  lastRefreshAt: lastLoanPaymentRefreshAt
});